// The Teams personal tab's host seam (standing-agents D13; task 15.4): a thin
// wrapper over @microsoft/teams-js so the tab can be tested without Teams, plus the
// Teams → AutomationAI theme mapping.

/** Teams' theme names → our palettes (default = light, dark = dark, contrast = hc). */
export function teamsThemeToMode(theme: string | null | undefined): 'light' | 'dark' | 'hc' {
  switch ((theme ?? '').toLowerCase()) {
    case 'dark':
      return 'dark';
    case 'contrast':
      return 'hc';
    default:
      return 'light';
  }
}

/** What the tab needs from the Teams client. */
export interface TeamsHost {
  /** Resolves when running inside Teams; rejects (or times out) outside it. */
  initialize: () => Promise<void>;
  theme: () => Promise<string | null>;
  onThemeChange: (handler: (theme: string) => void) => void;
  /** The Teams SSO token for this app (D13). */
  getAuthToken: () => Promise<string>;
}

/** How long to wait for Teams before deciding we're not inside it. */
export const TEAMS_INIT_TIMEOUT_MS = 5000;

/** How long to wait for Teams' sign-in token before saying sign-in didn't work. */
export const TEAMS_TOKEN_TIMEOUT_MS = 20000;

/** The Teams personal tab's pages, which no service worker may serve (see vite.config.ts). */
export function isTeamsTabPath(pathname: string): boolean {
  return pathname === '/teams' || pathname.startsWith('/teams/');
}

/**
 * Remove any service worker registered inside Teams. Earlier builds installed the app's
 * worker from the tab, and its cached page (framed 'none') then stopped Teams showing the
 * tab at all. Inside Teams' frame this registration is the tab's own; the browser app
 * registers its worker again on its next load.
 */
export async function dropServiceWorkers(container: ServiceWorkerContainer | undefined = navigator.serviceWorker): Promise<void> {
  if (!container) return;
  try {
    const registrations = await container.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // Not allowed here (e.g. storage blocked): nothing was registered either.
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Teams did not respond.')), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/** The real host, loaded on demand so teams-js stays out of every other chunk. */
export const realTeamsHost: TeamsHost = {
  initialize: async () => {
    const { app } = await import('@microsoft/teams-js');
    await withTimeout(app.initialize(), TEAMS_INIT_TIMEOUT_MS);
  },
  theme: async () => {
    const { app } = await import('@microsoft/teams-js');
    return (await app.getContext()).app.theme ?? null;
  },
  onThemeChange: (handler) => {
    void import('@microsoft/teams-js').then(({ app }) => app.registerOnThemeChangeHandler(handler));
  },
  getAuthToken: async () => {
    const { authentication } = await import('@microsoft/teams-js');
    // Without a timeout a host that never answers leaves the tab on its spinner.
    return withTimeout(authentication.getAuthToken(), TEAMS_TOKEN_TIMEOUT_MS);
  },
};
